import { useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { DocumentDuplicateIcon, PrinterIcon } from "@heroicons/react/24/outline";
import clsx from "clsx";
import { ArtikelImage } from "@/components/ArtikelImage";
import type { ArtikelRow } from "@/lib/artikel";
import { EhDataTableShell, SortableHeader, type SortState } from "@/components/dataTable";
import {
  FLOATING_TABLE_HEADER_Z_CLASS,
  useFloatingProjectTableHeader,
} from "@/pages/projekte/useFloatingProjectTableHeader";
import {
  ARTIKEL_FILTERABLE_COLUMNS,
  clampArtikelColumnWidth,
  getArtikelColumnDefaultWidth,
  getArtikelColumnLabel,
  renderArtikelCellValue,
  type ArtikelColumnKey,
} from "@/pages/artikel/artikelColumns";

/** Feste Breite der Auswahl-Checkbox – muss als `<col>` mitlaufen (table-layout: fixed). */
const ARTIKEL_SELECT_COL_WIDTH = 44;
const ARTIKEL_ETIKETT_COL_WIDTH = 96;

export interface ArtikelEtikettCartColumn {
  ids: Set<number>;
  onToggle: (row: ArtikelRow) => void;
}

function EtikettCartButton({
  row,
  inCart,
  onToggle,
}: {
  row: ArtikelRow;
  inCart: boolean;
  onToggle: (row: ArtikelRow) => void;
}) {
  return (
    <button
      type="button"
      className={clsx(
        "inline-flex h-8 w-8 items-center justify-center rounded-ios transition",
        inCart
          ? "bg-accent/15 text-accent"
          : "text-label-tertiary hover:bg-fill-tertiary hover:text-accent",
      )}
      aria-pressed={inCart}
      aria-label={
        inCart
          ? `Artikel ${row.hero_nr ?? row.id} aus dem Etikettenkorb entfernen`
          : `Artikel ${row.hero_nr ?? row.id} in den Etikettenkorb`
      }
      title={inCart ? "Aus dem Etikettenkorb entfernen" : "In den Etikettenkorb"}
      onClick={(e) => {
        e.stopPropagation();
        onToggle(row);
      }}
    >
      <PrinterIcon className="h-4 w-4" />
    </button>
  );
}

function CopyArtikelButton({
  row,
  copying,
  disabled,
  onCopy,
}: {
  row: ArtikelRow;
  copying: boolean;
  disabled: boolean;
  onCopy: (row: ArtikelRow) => void;
}) {
  return (
    <button
      type="button"
      className={clsx(
        "inline-flex h-8 w-8 items-center justify-center rounded-ios text-label-tertiary transition",
        "hover:bg-fill-tertiary hover:text-accent",
        "disabled:cursor-wait disabled:opacity-60",
      )}
      disabled={disabled}
      aria-label={
        copying
          ? `Artikel ${row.hero_nr ?? row.id} wird kopiert`
          : `Artikel ${row.hero_nr ?? row.id} kopieren`
      }
      title={copying ? "Artikel wird kopiert …" : "Artikel kopieren"}
      onClick={(e) => {
        e.stopPropagation();
        onCopy(row);
      }}
    >
      {copying ? (
        <span className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
      ) : (
        <DocumentDuplicateIcon className="h-4 w-4" />
      )}
    </button>
  );
}

export type ArtikelSelectionMode = "ids" | "allMatching";

/**
 * Treffergründe der hybriden Suche ("EAN", "Bezeichnung", …) als kleine
 * Badges. Nur gesetzt, wenn gerade gesucht wird – ohne Suche ändert sich
 * an der Tabelle nichts.
 */
function MatchReasonBadges({ reasons }: { reasons?: string[] | null }) {
  if (!reasons || reasons.length === 0) return null;
  return (
    <span className="mt-0.5 flex flex-wrap gap-1">
      {reasons.map((reason) => (
        <span
          key={reason}
          className="inline-flex items-center rounded-full bg-accent/10 px-1.5 py-0.5 text-[10px] font-medium leading-none text-accent"
        >
          {reason}
        </span>
      ))}
    </span>
  );
}

export interface ArtikelTableProps {
  rows: ArtikelRow[];
  columns: ArtikelColumnKey[];
  widths: Record<string, number>;
  loading: boolean;
  onColumnWidthChange?: (key: string, width: number) => void;
  onRowClick?: (row: ArtikelRow) => void;
  onLieferantClick?: (geschaeftspartnerId: number) => void;
  emptyText?: string;
  selectable?: boolean;
  selectionMode?: ArtikelSelectionMode;
  selectedIds?: Set<number>;
  excludedIds?: Set<number>;
  onToggleSelect?: (id: number) => void;
  onToggleSelectAll?: () => void;
  columnFilters?: Record<string, string>;
  onColumnFilterChange?: (param: string, value: string) => void;
  sort?: SortState | null;
  onSort?: (key: string) => void;
  etikettCart?: ArtikelEtikettCartColumn;
  onCopyArtikel?: (row: ArtikelRow) => void;
  copyingArtikelId?: number | null;
}

function isRowSelected(
  id: number,
  mode: ArtikelSelectionMode,
  selectedIds?: Set<number>,
  excludedIds?: Set<number>,
): boolean {
  if (mode === "allMatching") return !excludedIds?.has(id);
  return selectedIds?.has(id) ?? false;
}

export function ArtikelTable({
  rows,
  columns,
  widths,
  loading,
  onColumnWidthChange,
  onRowClick,
  onLieferantClick,
  emptyText = "Keine Artikel gefunden.",
  selectable = false,
  selectionMode = "ids",
  selectedIds,
  excludedIds,
  onToggleSelect,
  onToggleSelectAll,
  columnFilters,
  onColumnFilterChange,
  sort,
  onSort,
  etikettCart,
  onCopyArtikel,
  copyingArtikelId = null,
}: ArtikelTableProps) {
  const colSpan = columns.length + (selectable ? 1 : 0) + (etikettCart ? 1 : 0);
  const headerChecked = selectable && selectionMode === "allMatching";
  const headerIndeterminate =
    headerChecked && (excludedIds?.size ?? 0) > 0 && rows.some((r) => excludedIds?.has(r.id));

  return (
    <div>
      <div className="hidden md:block">
        <DesktopTable
          rows={rows}
          columns={columns}
          widths={widths}
          loading={loading}
          emptyText={emptyText}
          colSpan={colSpan}
          selectable={selectable}
          selectionMode={selectionMode}
          selectedIds={selectedIds}
          excludedIds={excludedIds}
          headerChecked={headerChecked}
          headerIndeterminate={Boolean(headerIndeterminate)}
          onToggleSelect={onToggleSelect}
          onToggleSelectAll={onToggleSelectAll}
          columnFilters={columnFilters}
          onColumnFilterChange={onColumnFilterChange}
          onColumnWidthChange={onColumnWidthChange}
          onRowClick={onRowClick}
          onLieferantClick={onLieferantClick}
          sort={sort}
          onSort={onSort}
          etikettCart={etikettCart}
          onCopyArtikel={onCopyArtikel}
          copyingArtikelId={copyingArtikelId}
        />
      </div>
      <div className="md:hidden">
        <MobileCards
          rows={rows}
          columns={columns}
          loading={loading}
          emptyText={emptyText}
          selectable={selectable}
          selectionMode={selectionMode}
          selectedIds={selectedIds}
          excludedIds={excludedIds}
          onToggleSelect={onToggleSelect}
          onRowClick={onRowClick}
          onLieferantClick={onLieferantClick}
          etikettCart={etikettCart}
          onCopyArtikel={onCopyArtikel}
          copyingArtikelId={copyingArtikelId}
        />
      </div>
    </div>
  );
}

interface DesktopTableProps {
  rows: ArtikelRow[];
  columns: ArtikelColumnKey[];
  widths: Record<string, number>;
  loading: boolean;
  emptyText: string;
  colSpan: number;
  selectable?: boolean;
  selectionMode: ArtikelSelectionMode;
  selectedIds?: Set<number>;
  excludedIds?: Set<number>;
  headerChecked?: boolean;
  headerIndeterminate?: boolean;
  onToggleSelect?: (id: number) => void;
  onToggleSelectAll?: () => void;
  columnFilters?: Record<string, string>;
  onColumnFilterChange?: (param: string, value: string) => void;
  onColumnWidthChange?: (key: string, width: number) => void;
  onRowClick?: (row: ArtikelRow) => void;
  onLieferantClick?: (geschaeftspartnerId: number) => void;
  sort?: SortState | null;
  onSort?: (key: string) => void;
  etikettCart?: ArtikelEtikettCartColumn;
  onCopyArtikel?: (row: ArtikelRow) => void;
  copyingArtikelId?: number | null;
}

function DesktopThead({
  columns,
  activeResize,
  onResizePointerDown,
  omitResizers,
  selectable,
  headerChecked,
  headerIndeterminate,
  onToggleSelectAll,
  columnFilters,
  onColumnFilterChange,
  sort,
  onSort,
  etikettCart,
}: {
  columns: ArtikelColumnKey[];
  activeResize: string | null;
  onResizePointerDown: (e: React.PointerEvent, colKey: string, idx: number) => void;
  omitResizers?: boolean;
  selectable?: boolean;
  headerChecked?: boolean;
  headerIndeterminate?: boolean;
  onToggleSelectAll?: () => void;
  columnFilters?: Record<string, string>;
  onColumnFilterChange?: (param: string, value: string) => void;
  sort?: SortState | null;
  onSort?: (key: string) => void;
  etikettCart?: ArtikelEtikettCartColumn;
}) {
  const showFilters = Boolean(onColumnFilterChange);
  return (
    <thead className="eh-data-thead bg-card-elevated text-xs uppercase tracking-wide text-label-secondary">
      <tr>
        {selectable ? (
          <th
            className="eh-data-th px-2 py-2 text-center"
            style={{ width: ARTIKEL_SELECT_COL_WIDTH, minWidth: ARTIKEL_SELECT_COL_WIDTH }}
          >
            <input
              type="checkbox"
              checked={Boolean(headerChecked)}
              ref={(el) => {
                if (el) el.indeterminate = Boolean(headerIndeterminate);
              }}
              disabled={columns.length === 0}
              onChange={() => onToggleSelectAll?.()}
              aria-label="Alle gefilterten Artikel auswählen"
              title="Alle gefilterten Artikel auswählen"
            />
          </th>
        ) : null}
        {etikettCart ? (
          <th
            className="eh-data-th px-1 py-2 text-center"
            style={{ width: ARTIKEL_ETIKETT_COL_WIDTH, minWidth: ARTIKEL_ETIKETT_COL_WIDTH }}
            title="Etikettenkorb und Kopieren"
          >
            <span className="inline-flex items-center justify-center gap-2">
              <PrinterIcon className="h-4 w-4" />
              <DocumentDuplicateIcon className="h-4 w-4" />
            </span>
            <span className="sr-only">Etikettenkorb und Kopieren</span>
          </th>
        ) : null}
        {columns.map((col, idx) => (
          <th
            key={col}
            scope="col"
            className="eh-data-th whitespace-nowrap px-3 py-2 text-left font-semibold leading-snug"
          >
            {[
              "id",
              "hero_nr",
              "hero_base_name",
              "hero_base_description",
              "hero_base_ean",
              "hero_base_category",
              "hero_base_manufacturer",
              "hero_base_manufacturer_nr",
              "hero_base_supplier_nr",
              "hero_supply_operator_name",
              "lieferant_geschaeftspartner_name",
              "preis_verkaufspreis",
              "preis_nettopreis",
            ].includes(col) && onSort ? (
              <SortableHeader
                label={getArtikelColumnLabel(col)}
                columnKey={col}
                sort={sort ?? null}
                onSort={onSort}
              />
            ) : (
              <span className="block truncate" title={getArtikelColumnLabel(col)}>
                {getArtikelColumnLabel(col)}
              </span>
            )}
            {!omitResizers && (
              <span
                className={clsx("eh-data-resizer", activeResize === col && "is-active")}
                role="separator"
                aria-orientation="vertical"
                title="Spaltenbreite ziehen"
                onPointerDown={(e) => onResizePointerDown(e, col, idx)}
              />
            )}
          </th>
        ))}
      </tr>
      {showFilters ? (
        <tr className="normal-case tracking-normal">
          {selectable ? (
            <th className="eh-data-th px-1 py-1" style={{ width: ARTIKEL_SELECT_COL_WIDTH }} />
          ) : null}
          {etikettCart ? (
            <th className="eh-data-th px-1 py-1" style={{ width: ARTIKEL_ETIKETT_COL_WIDTH }} />
          ) : null}
          {columns.map((col) => {
            const meta = ARTIKEL_FILTERABLE_COLUMNS[col];
            if (!meta) {
              return <th key={`f-${col}`} className="eh-data-th px-1 py-1" />;
            }
            const value = columnFilters?.[meta.param] ?? "";
            if (meta.kind === "bool") {
              return (
                <th key={`f-${col}`} className="eh-data-th px-1 py-1">
                  <select
                    className="w-full min-w-0 rounded border border-separator bg-card px-1 py-1 text-[11px] font-normal text-label"
                    value={value}
                    onChange={(e) => onColumnFilterChange?.(meta.param, e.target.value)}
                    aria-label={`${getArtikelColumnLabel(col)} filtern`}
                  >
                    <option value="">Alle</option>
                    <option value="true">Ja</option>
                    <option value="false">Nein</option>
                  </select>
                </th>
              );
            }
            return (
              <th key={`f-${col}`} className="eh-data-th px-1 py-1">
                <input
                  type="search"
                  value={value}
                  onChange={(e) => onColumnFilterChange?.(meta.param, e.target.value)}
                  placeholder="Filtern…"
                  aria-label={`${getArtikelColumnLabel(col)} filtern`}
                  className="w-full min-w-0 rounded border border-separator bg-card px-1.5 py-1 text-[11px] font-normal normal-case tracking-normal text-label placeholder:text-label-tertiary"
                />
              </th>
            );
          })}
        </tr>
      ) : null}
    </thead>
  );
}

function DesktopTable({
  rows,
  columns,
  widths,
  loading,
  emptyText,
  colSpan,
  selectable,
  selectionMode,
  selectedIds,
  excludedIds,
  headerChecked,
  headerIndeterminate,
  onToggleSelect,
  onToggleSelectAll,
  columnFilters,
  onColumnFilterChange,
  onColumnWidthChange,
  onRowClick,
  onLieferantClick,
  sort,
  onSort,
  etikettCart,
  onCopyArtikel,
  copyingArtikelId = null,
}: DesktopTableProps) {
  const dataWidths = useMemo(
    () =>
      columns.map((col) => {
        const stored = Number(widths?.[col]);
        return Number.isFinite(stored) && stored > 0
          ? clampArtikelColumnWidth(stored)
          : getArtikelColumnDefaultWidth(col);
      }),
    [columns, widths],
  );

  const colRefs = useRef<Record<string, HTMLTableColElement | null>>({});
  const floatingColRefs = useRef<Record<string, HTMLTableColElement | null>>({});
  const [activeResize, setActiveResize] = useState<string | null>(null);
  const tableWrapRef = useRef<HTMLDivElement | null>(null);
  const tableRef = useRef<HTMLTableElement | null>(null);

  const floatingSyncKey = useMemo(
    () =>
      [
        columns.join("|"),
        dataWidths.join(","),
        rows.length,
        selectable ? "1" : "0",
        etikettCart ? "e" : "",
        onColumnFilterChange ? "f" : "",
      ].join("§"),
    [columns, dataWidths, rows.length, selectable, etikettCart, onColumnFilterChange],
  );

  const floating = useFloatingProjectTableHeader(tableWrapRef, tableRef, {
    enabled: columns.length > 0,
    syncKey: floatingSyncKey,
  });

  function startResize(event: React.PointerEvent, colKey: string, idx: number) {
    event.preventDefault();
    event.stopPropagation();
    const target = event.currentTarget as HTMLElement;
    target.setPointerCapture(event.pointerId);
    setActiveResize(colKey);

    const startX = event.clientX;
    const startWidth = dataWidths[idx] ?? getArtikelColumnDefaultWidth(colKey);
    const colEl = colRefs.current[colKey];
    let lastWidth = startWidth;

    function applyWidth(width: number) {
      lastWidth = clampArtikelColumnWidth(width);
      if (colEl) colEl.style.width = `${lastWidth}px`;
      const floatCol = floatingColRefs.current[colKey];
      if (floatCol) floatCol.style.width = `${lastWidth}px`;
    }

    function onMove(e: PointerEvent) {
      applyWidth(startWidth + (e.clientX - startX));
    }

    function onUp() {
      target.releasePointerCapture?.(event.pointerId);
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
      setActiveResize(null);
      if (lastWidth !== startWidth) onColumnWidthChange?.(colKey, lastWidth);
    }

    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
  }

  return (
    <>
      <EhDataTableShell ref={tableWrapRef}>
        <table ref={tableRef} className="eh-data-table divide-y divide-separator text-sm">
          <colgroup>
            {selectable ? <col style={{ width: `${ARTIKEL_SELECT_COL_WIDTH}px` }} /> : null}
            {etikettCart ? <col style={{ width: `${ARTIKEL_ETIKETT_COL_WIDTH}px` }} /> : null}
            {columns.map((col, idx) => (
              <col
                key={col}
                ref={(el) => {
                  colRefs.current[col] = el;
                }}
                style={{ width: `${dataWidths[idx]}px` }}
              />
            ))}
          </colgroup>

          <DesktopThead
            columns={columns}
            activeResize={activeResize}
            onResizePointerDown={startResize}
            omitResizers={false}
            selectable={selectable}
            headerChecked={headerChecked}
            headerIndeterminate={headerIndeterminate}
            onToggleSelectAll={onToggleSelectAll}
            columnFilters={columnFilters}
            onColumnFilterChange={onColumnFilterChange}
            sort={sort}
            onSort={onSort}
            etikettCart={etikettCart}
          />

          <tbody className="divide-y divide-separator bg-card">
            {rows.length === 0 && !loading ? (
              <tr>
                <td colSpan={colSpan} className="px-3 py-8 text-center text-sm text-label-tertiary">
                  {emptyText}
                </td>
              </tr>
            ) : null}
            {rows.map((row) => {
              const rowSelected = isRowSelected(row.id, selectionMode, selectedIds, excludedIds);
              return (
              <tr
                key={row.id}
                className={clsx(
                  "eh-data-row align-middle transition hover:bg-fill-secondary/40",
                  onRowClick && "cursor-pointer",
                  selectable && rowSelected && "bg-accent/5",
                )}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
              >
                {selectable ? (
                  <td
                    className="eh-data-body-cell px-2 py-2 text-center align-middle"
                    style={{ width: ARTIKEL_SELECT_COL_WIDTH }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <input
                      type="checkbox"
                      checked={rowSelected}
                      onChange={() => onToggleSelect?.(row.id)}
                      aria-label={`Artikel ${row.hero_nr ?? row.id} auswählen`}
                    />
                  </td>
                ) : null}
                {etikettCart ? (
                  <td
                    className="eh-data-body-cell px-1 py-2 text-center align-middle"
                    style={{ width: ARTIKEL_ETIKETT_COL_WIDTH }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <span className="inline-flex items-center justify-center">
                      <EtikettCartButton
                        row={row}
                        inCart={etikettCart.ids.has(row.id)}
                        onToggle={etikettCart.onToggle}
                      />
                      {onCopyArtikel ? (
                        <CopyArtikelButton
                          row={row}
                          copying={copyingArtikelId === row.id}
                          disabled={copyingArtikelId != null}
                          onCopy={onCopyArtikel}
                        />
                      ) : null}
                    </span>
                  </td>
                ) : null}
                {columns.map((col) => {
                  const cell = renderArtikelCellValue(row, col);
                  return (
                    <td
                      key={col}
                      className={clsx(
                        "eh-data-body-cell min-w-0 align-middle whitespace-normal break-words px-3 py-2 text-label",
                        cell.mono && "font-mono tabular-nums text-xs",
                        cell.alignRight && "text-right",
                      )}
                      title={cell.value !== "—" ? cell.value : undefined}
                    >
                      {cell.kind === "image" ? (
                        <ArtikelImage
                          src={row.hero_base_image_src}
                          alt={row.hero_base_name || row.hero_nr || ""}
                          size="sm"
                          expandable
                        />
                      ) : cell.kind === "lieferant" && cell.lieferantGeschaeftspartnerId ? (
                        <button
                          type="button"
                          className="text-left font-medium text-accent hover:underline"
                          onClick={(e) => {
                            e.stopPropagation();
                            onLieferantClick?.(cell.lieferantGeschaeftspartnerId!);
                          }}
                        >
                          {cell.value}
                        </button>
                      ) : col === "hero_base_name" ? (
                        <span className="flex items-start gap-2">
                          {columns.includes("hero_base_image_src") ? null : (
                            <ArtikelImage
                              src={row.hero_base_image_src}
                              alt=""
                              size="sm"
                              expandable
                            />
                          )}
                          <span className="block min-w-0 break-words">{cell.value}</span>
                        </span>
                      ) : (
                        <span className="block w-full break-words">{cell.value}</span>
                      )}
                      {col === "hero_base_name" && row.deleted_at ? (
                        <span className="mt-1 inline-flex rounded-full bg-fill-secondary px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-label-secondary">
                          gelöscht
                        </span>
                      ) : null}
                      {col === "hero_base_name" ? (
                        <MatchReasonBadges reasons={row.match_reasons} />
                      ) : null}
                    </td>
                  );
                })}
              </tr>
              );
            })}
          </tbody>
        </table>
      </EhDataTableShell>

      {floating &&
        typeof document !== "undefined" &&
        createPortal(
          <div
            className={`pointer-events-none fixed ${FLOATING_TABLE_HEADER_Z_CLASS} overflow-hidden rounded-b-ios border border-t-0 border-separator bg-card-elevated shadow-[0_8px_18px_rgba(15,23,42,0.22)]`}
            style={{ top: floating.top, left: floating.left, width: floating.width }}
            aria-hidden
          >
            <div
              className="pointer-events-auto eh-floating-table-header-sync"
              style={{
                width: floating.tableWidth,
                transform: `translateX(${-floating.scrollLeft}px)`,
                ["--eh-table-scroll-left" as string]: `${floating.scrollLeft}px`,
              }}
            >
              <table className="eh-data-table w-full text-sm">
                <colgroup>
                  {selectable ? <col style={{ width: `${ARTIKEL_SELECT_COL_WIDTH}px` }} /> : null}
                  {etikettCart ? <col style={{ width: `${ARTIKEL_ETIKETT_COL_WIDTH}px` }} /> : null}
                  {columns.map((col, idx) => (
                    <col
                      key={col}
                      ref={(el) => {
                        floatingColRefs.current[col] = el;
                      }}
                      style={{ width: `${dataWidths[idx]}px` }}
                    />
                  ))}
                </colgroup>
                <DesktopThead
                  columns={columns}
                  activeResize={activeResize}
                  onResizePointerDown={startResize}
                  omitResizers={false}
                  selectable={selectable}
                  headerChecked={headerChecked}
                  headerIndeterminate={headerIndeterminate}
                  onToggleSelectAll={onToggleSelectAll}
                  columnFilters={columnFilters}
                  onColumnFilterChange={onColumnFilterChange}
                  sort={sort}
                  onSort={onSort}
                  etikettCart={etikettCart}
                />
              </table>
            </div>
          </div>,
          document.body,
        )}
    </>
  );
}

function MobileCards({
  rows,
  columns,
  loading,
  emptyText,
  selectable,
  selectionMode,
  selectedIds,
  excludedIds,
  onToggleSelect,
  onRowClick,
  onLieferantClick,
  etikettCart,
  onCopyArtikel,
  copyingArtikelId = null,
}: {
  rows: ArtikelRow[];
  columns: ArtikelColumnKey[];
  loading: boolean;
  emptyText: string;
  selectable?: boolean;
  selectionMode: ArtikelSelectionMode;
  selectedIds?: Set<number>;
  excludedIds?: Set<number>;
  onToggleSelect?: (id: number) => void;
  onRowClick?: (row: ArtikelRow) => void;
  onLieferantClick?: (geschaeftspartnerId: number) => void;
  etikettCart?: ArtikelEtikettCartColumn;
  onCopyArtikel?: (row: ArtikelRow) => void;
  copyingArtikelId?: number | null;
}) {
  if (rows.length === 0 && !loading) {
    return (
      <div className="rounded-ios border border-dashed border-separator px-4 py-8 text-center text-sm text-label-secondary">
        {emptyText}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {rows.map((row) => {
        const primary = renderArtikelCellValue(row, "hero_nr");
        const name = renderArtikelCellValue(row, "hero_base_name");
        return (
          <div
            key={row.id}
            className={clsx(
              "rounded-ios border border-separator bg-card px-4 py-3 shadow-ios",
              onRowClick && "cursor-pointer active:bg-fill-secondary/40",
            )}
            onClick={onRowClick ? () => onRowClick(row) : undefined}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="flex min-w-0 items-start gap-2">
                {selectable ? (
                  <input
                    type="checkbox"
                    className="mt-0.5"
                    checked={isRowSelected(row.id, selectionMode, selectedIds, excludedIds)}
                    onClick={(e) => e.stopPropagation()}
                    onChange={() => onToggleSelect?.(row.id)}
                  />
                ) : null}
                <ArtikelImage
                  src={row.hero_base_image_src}
                  alt=""
                  size="sm"
                  expandable
                />
                <span className="font-mono text-sm font-semibold text-label">{primary.value}</span>
              </div>
              <div className="flex min-w-0 items-start justify-end gap-2">
                {name.value !== "—" ? (
                  <span className="min-w-0 text-right text-sm text-label-secondary">
                    {name.value}
                    {row.deleted_at ? (
                      <span className="ml-1 inline-flex rounded-full bg-fill-secondary px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-label-tertiary">
                        gelöscht
                      </span>
                    ) : null}
                  </span>
                ) : null}
                {etikettCart ? (
                  <EtikettCartButton
                    row={row}
                    inCart={etikettCart.ids.has(row.id)}
                    onToggle={etikettCart.onToggle}
                  />
                ) : null}
                {onCopyArtikel ? (
                  <CopyArtikelButton
                    row={row}
                    copying={copyingArtikelId === row.id}
                    disabled={copyingArtikelId != null}
                    onCopy={onCopyArtikel}
                  />
                ) : null}
              </div>
            </div>
            <MatchReasonBadges reasons={row.match_reasons} />
            <dl className="mt-2 grid gap-1 text-sm">
              {columns
                .filter(
                  (col) =>
                    col !== "hero_nr" && col !== "hero_base_name" && col !== "hero_base_image_src",
                )
                .map((col) => {
                  const cell = renderArtikelCellValue(row, col);
                  return (
                    <div key={col} className="flex gap-2">
                      <dt className="w-28 shrink-0 text-label-tertiary">{getArtikelColumnLabel(col)}</dt>
                      <dd className="min-w-0 break-words text-label">
                        {cell.kind === "lieferant" && cell.lieferantGeschaeftspartnerId ? (
                          <button
                            type="button"
                            className="font-medium text-accent hover:underline"
                            onClick={(e) => {
                              e.stopPropagation();
                              onLieferantClick?.(cell.lieferantGeschaeftspartnerId!);
                            }}
                          >
                            {cell.value}
                          </button>
                        ) : (
                          cell.value
                        )}
                      </dd>
                    </div>
                  );
                })}
            </dl>
          </div>
        );
      })}
    </div>
  );
}
